'use client';

import NextButton from '@/components/Button/NextButton';
import Input from '@/components/Input/Input';
import { FormData, USStates } from '@/types/form';
import { fieldRules } from '@/utils/form';
import {
  Autocomplete,
  Libraries,
  useJsApiLoader,
} from '@react-google-maps/api';
import { memo, useRef } from 'react';
import {
  Control,
  UseFormRegister,
  UseFormSetValue,
  useFormState,
  UseFormTrigger,
} from 'react-hook-form';
import generalStyles from '../FormBody.module.scss';
import AddressFormManual from './AddressFormManual';
import StateSelect from './StateSelect/StateSelect';
import styles from './StepSix.module.scss';

const libraries: Libraries = ['places'];

interface Props {
  register?: UseFormRegister<FormData>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control?: Control<FormData, any, FormData>;
  setValue?: UseFormSetValue<FormData>;
  trigger?: UseFormTrigger<FormData>;
  handleSubmit?: (e?: React.BaseSyntheticEvent) => Promise<void>;
  closeModal?: () => void;
  formData: FormData;
}

const AddressFormModal = ({
  register,
  control,
  setValue,
  trigger,
  handleSubmit,
  closeModal,
  formData,
}: Props) => {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const { errors } = useFormState({ control });

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY as string,
    libraries,
  });

  const extractAddressDetails = (place: google.maps.places.PlaceResult) => {
    let city = '';
    let state = '';
    let zip = '';

    place.address_components?.forEach((component) => {
      const types = component.types;
      if (types.includes('locality')) {
        city = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        state = component.short_name;
      }
      if (types.includes('postal_code')) {
        zip = component.long_name.slice(0, 5);
      }
    });

    setValue?.('city', city);
    setValue?.('state', state as USStates);
    setValue?.('zip', zip);
    trigger?.(['city', 'state', 'zip']);
  };

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.address_components) {
        extractAddressDetails(place);
      }
    }
  };

  const handleLoad = (autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const handleChangeAddressVariant = () => {
    setValue?.('address_variant', 'manual');
    trigger?.('address_variant');
  };

  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isValid = await trigger?.();
    if (isValid) {
      await handleSubmit?.();
      closeModal?.();
    }
  };

  return (
    <form className={styles.form} onSubmit={submitForm}>
      <div className={styles.inputsWrapper}>
        {isLoaded && (
          <>
            <div className={styles.addressWrapper}>
              <Autocomplete
                onLoad={handleLoad}
                onPlaceChanged={handlePlaceChanged}
                options={{
                  componentRestrictions: { country: 'us' },
                }}
              >
                {register && (
                  <Input
                    label="Street address"
                    placeholder="Your street"
                    type="text"
                    id="address"
                    register={register('address', fieldRules.address)}
                    error={errors?.address}
                  />
                )}
              </Autocomplete>
              <div className={styles.label}>
                <span>Can&apos;t find your address?&nbsp;</span>
                <button
                  type="button"
                  onClick={handleChangeAddressVariant}
                  className={styles.variantButton}
                >
                  Enter it manually
                </button>
              </div>
            </div>
            <div className={styles.inputsRow}>
              {register && (
                <>
                  <Input
                    label="Apt/Suite"
                    placeholder="Your apt/Suite"
                    type="text"
                    id="apt"
                    register={register('apt')}
                    error={errors?.apt}
                  />
                  <Input
                    label="City"
                    placeholder="Your city"
                    type="text"
                    id="city"
                    register={register('city', fieldRules.city)}
                    error={errors?.city}
                  />
                </>
              )}
            </div>
            <div className={styles.inputsRow}>
              {register && (
                <>
                  <StateSelect
                    formData={formData}
                    label="State"
                    placeholder="Your state"
                    id="state"
                    register={register('state', fieldRules.state)}
                    error={errors?.state}
                    setValue={setValue}
                    trigger={trigger}
                  />
                  <Input
                    label="Zip code"
                    placeholder="Your zip code"
                    type="text"
                    id="zip"
                    register={register('zip', {
                      ...fieldRules.zip,
                      onChange: (e) => {
                        const { target } = e;
                        target.value = target.value
                          .replace(/\D/g, '')
                          .slice(0, 5);
                      },
                    })}
                    error={errors?.zip}
                  />
                </>
              )}
            </div>
          </>
        )}
        {loadError && register && (
          <AddressFormManual
            formData={formData}
            trigger={trigger}
            register={register}
            errors={errors}
            setValue={setValue}
            hiddenSwitcher
          />
        )}
      </div>
      <NextButton
        buttonType="submit"
        type="button"
        text="Apply changes"
        styles={`${generalStyles.button} ${generalStyles.smallButton}`}
      />
    </form>
  );
};

export default memo(AddressFormModal);
